import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { hrFinanceAPI, employeeAPI, monitoringAPI, dashboardAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DollarSign, 
  Users, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  Building2,
  PieChart,
  BarChart3,
  GraduationCap,
  UserPlus,
  Heart,
  Shield
} from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line } from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#4F46E5', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#6366F1', '#14B8A6'];

const HRFinance = () => {
  const { user } = useAuth();
  const [financeData, setFinanceData] = useState(null);
  const [salaryData, setSalaryData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [financeRes, salaryRes] = await Promise.all([
        hrFinanceAPI.getDashboard(),
        hrFinanceAPI.getSalaryBreakdown()
      ]);
      setFinanceData(financeRes.data);
      setSalaryData(salaryRes.data);
    } catch (error) {
      console.error('HR Finance fetch error:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `R ${amount?.toLocaleString() || 0}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading financial data...</p>
        </div>
      </div>
    );
  }

  const summary = {
    total_monthly_payroll: financeData?.monthly_payroll || 0,
    total_annual_payroll: financeData?.annual_payroll || 0,
    total_employees: financeData?.total_employees || 0,
    average_salary: financeData?.average_salary || 0
  };
  const deptBreakdown = financeData?.department_breakdown || [];
  const training = financeData?.training_budget || {};
  const recruitment = financeData?.recruitment_budget || {};
  const risk = financeData?.risk_analysis || {};

  return (
    <div className="space-y-6" data-testid="hr-finance-page">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Monthly Payroll</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {formatCurrency(summary.total_monthly_payroll)}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm">Annual Payroll</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {formatCurrency(summary.total_annual_payroll)}
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Employees</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {summary.total_employees}
                </p>
              </div>
              <Users className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Average Salary</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {formatCurrency(summary.average_salary)}
                </p>
              </div>
              <BarChart3 className="w-10 h-10 text-violet-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="departments">Departments</TabsTrigger>
          <TabsTrigger value="budgets">Budgets</TabsTrigger>
          <TabsTrigger value="risk">Risk Analysis</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Department Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Payroll by Department</CardTitle>
                <CardDescription>Monthly salary distribution</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={deptBreakdown}
                        dataKey="total_salary"
                        nameKey="department"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ department, percent }) => `${department} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {deptBreakdown.map((entry, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Salary Ranges */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Salary Distribution</CardTitle>
                <CardDescription>Employees by salary range</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={salaryData?.salary_ranges || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="range" stroke="#94A3B8" fontSize={10} />
                      <YAxis stroke="#94A3B8" fontSize={12} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#4F46E5" radius={[4, 4, 0, 0]} name="Employees" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Departments */}
        <TabsContent value="departments">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Department Breakdown</CardTitle>
              <CardDescription>Detailed salary analysis by department</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Department</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Employees</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Total Salary</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Average</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">% of Payroll</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deptBreakdown.map((dept, index) => (
                      <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                            <span className="font-medium text-slate-900">{dept.department}</span>
                          </div>
                        </td>
                        <td className="text-right py-3 px-4 text-slate-600">{dept.employee_count}</td>
                        <td className="text-right py-3 px-4 font-medium text-slate-900">{formatCurrency(dept.total_salary)}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{formatCurrency(dept.avg_salary)}</td>
                        <td className="text-right py-3 px-4">
                          <Badge className="bg-indigo-100 text-indigo-700">
                            {((dept.total_salary / summary.total_monthly_payroll) * 100).toFixed(1)}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Budgets */}
        <TabsContent value="budgets">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Training Budget */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Training Budget</CardTitle>
                    <CardDescription>Annual training expenditure</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Budget</span>
                  <span className="font-bold text-slate-900">{formatCurrency(training.allocated)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Actual Spend</span>
                  <span className="font-bold text-emerald-600">{formatCurrency(training.spent)}</span>
                </div>
                <Progress value={training.allocated ? (training.spent / training.allocated * 100) : 0} className="h-3" />
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500">{training.allocated ? Math.round(training.spent / training.allocated * 100) : 0}% utilized</span>
                  <Badge className={training.remaining > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                    {training.remaining > 0 ? 'Under budget' : 'Over budget'}: {formatCurrency(Math.abs(training.remaining || 0))}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Recruitment Budget */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Recruitment Budget</CardTitle>
                    <CardDescription>Annual hiring expenditure</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Budget</span>
                  <span className="font-bold text-slate-900">{formatCurrency(recruitment.allocated)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Actual Spend</span>
                  <span className="font-bold text-emerald-600">{formatCurrency(recruitment.spent)}</span>
                </div>
                <Progress value={recruitment.allocated ? (recruitment.spent / recruitment.allocated * 100) : 0} className="h-3" />
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500">{recruitment.allocated ? Math.round(recruitment.spent / recruitment.allocated * 100) : 0}% utilized</span>
                  <Badge className={recruitment.remaining > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                    {recruitment.remaining > 0 ? 'Under budget' : 'Over budget'}: {formatCurrency(Math.abs(recruitment.remaining || 0))}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Risk Analysis */}
        <TabsContent value="risk">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className={risk.burnout_rate > 15 ? 'border-red-200 bg-red-50' : ''}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Heart className={`w-8 h-8 ${risk.burnout_rate > 15 ? 'text-red-500' : 'text-amber-500'}`} />
                  <Badge className={risk.burnout_rate > 15 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                    {risk.burnout_rate > 15 ? 'High Risk' : 'Moderate'}
                  </Badge>
                </div>
                <p className="text-sm text-slate-500">Burnout Rate</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {risk.burnout_rate}%
                </p>
                <p className="text-xs text-slate-400 mt-2">{risk.high_risk_count} employees at risk</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <TrendingDown className="w-8 h-8 text-orange-500" />
                  <Badge className="bg-orange-100 text-orange-700">Monitor</Badge>
                </div>
                <p className="text-sm text-slate-500">Turnover Risk</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {risk.turnover_risk}%
                </p>
                <p className="text-xs text-slate-400 mt-2">Based on industry average</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Shield className="w-8 h-8 text-emerald-500" />
                  <Badge className="bg-emerald-100 text-emerald-700">Good</Badge>
                </div>
                <p className="text-sm text-slate-500">Compliance Score</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {risk.compliance_score}%
                </p>
                <p className="text-xs text-slate-400 mt-2">Regulatory compliance</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-indigo-500 to-violet-500 text-white border-none">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Building2 className="w-8 h-8 text-indigo-200" />
                </div>
                <p className="text-indigo-100 text-sm">Overall HR Health</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Good
                </p>
                <p className="text-xs text-indigo-200 mt-2">Based on all metrics</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HRFinance;
